import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import moment from 'moment-timezone';

import Client, { ConversionSource, LeadStage } from '@spike/client-model';
import { Period } from '@spike/model';
import { RootState } from 'store';

import {
    fetchLeadsThunk,
    fetchLeadsStagesCountThunk,
    clearLeadsThunk,
    saveClientThunk,
    logicalDeleteClientThunk,
    ClientsStatus
} from '@spike/clients-action';

import { useApiClientWrapper } from 'hooks';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface BookingNew {
    clientId: number;
    parentName: string;
    petId?: number;
}

export interface FilterDrawerState {
    filterOperator: 'and' | 'or';
    creationDate: { enabled: boolean; period: Period | null };
    lastContacted: { enabled: boolean; period: Period | null };
    source: { enabled: boolean; selectedSources: string[] };
    stage: { enabled: boolean; selectedStages: string[] };
}

const defaultFilterDrawerState: FilterDrawerState = {
    filterOperator: 'and',
    creationDate: { enabled: false, period: null },
    lastContacted: { enabled: false, period: null },
    source: { enabled: false, selectedSources: [] },
    stage: { enabled: false, selectedStages: [] }
};

// ─── Context interfaces ─────────────────────────────────────────────────────────

interface LeadsDataContextProps {
    leads: Array<Client>;
    isLoading: boolean;
    isFirstRender: boolean;
    status: ClientsStatus;

    page: number;
    size: number;
    totalPages: number;
    totalRecords: number;
    hasMorePages: boolean;
    setPage: (page: number) => void;
    setSize: (size: number) => void;

    stageCounts: Record<string, number>;

    selectedStage: string;
    handleStageChange: (stage: string) => void;

    selectedSource: string;
    handleSourceChange: (source: string) => void;

    searchText: string;
    searchInputKey: number;
    handleSearchChange: (text: string) => void;

    order: string;
    handleOrderChange: (order: string) => void;

    appliedFiltersCount: number;
    hasActiveFilters: boolean;
    isGlobalEmpty: boolean;
}

interface LeadsActionsContextProps {
    saveLead: (lead: Client) => void;
    deleteLead: (leadId: number) => void;
    convertLead: (lead: Client) => void;
    isConverting: boolean;
    refreshLeads: () => void;
    fetchStagesCount: () => void;
    isSaving: boolean;
    openAddForm: () => void;
    openEditForm: (lead: Client) => void;
    openConvert: (lead: Client) => void;
    openBooking: (lead: Client) => void;
    openSms: (lead: Client) => void;
    openShareSendPanel: (lead: Client) => void;
    openDetail: (lead: Client) => void;
    closeDetail: () => void;
}

interface LeadsUIContextProps {
    showAddLead: boolean;
    editingLead: Client | null;
    closeForm: () => void;

    showFilterLead: boolean;
    openFilterDrawer: () => void;
    closeFilterDrawer: () => void;
    draftFilters: FilterDrawerState;
    setDraftFilters: React.Dispatch<React.SetStateAction<FilterDrawerState>>;
    appliedFilters: FilterDrawerState;
    handleApplyFilterDrawer: (state: FilterDrawerState) => void;
    handleClearFilterDrawer: () => void;

    showNewBooking: BookingNew | undefined;
    closeBooking: () => void;
    updateBookingPet: (petId: number) => void;

    smsClient: Client | null;
    closeSms: () => void;

    showShareSendPanel: boolean;
    shareSendLead: Client | null;
    closeShareSendPanel: () => void;

    convertingLead: Client | null;
    closeConvert: () => void;

    /**
     * Lead Detail has no URL of its own — it replaces the list in place, so the
     * selected lead lives here rather than in a route param.
     */
    detailLead: Client | null;
}

// ─── Contexts ──────────────────────────────────────────────────────────────────

const LeadsDataContext = React.createContext<LeadsDataContextProps | null>(null);
const LeadsActionsContext = React.createContext<LeadsActionsContextProps | null>(null);
const LeadsUIContext = React.createContext<LeadsUIContextProps | null>(null);

// ─── Provider ──────────────────────────────────────────────────────────────────

interface LeadsProviderProps {
    children: React.ReactNode;
}

export const LeadsProvider = ({ children }: LeadsProviderProps) => {
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();

    const { leads, leadsTotalPages, leadsTotalRecords, status, leadsStagesCount } = useSelector(
        (state: RootState) => state.clients
    );

    const location = useLocation();

    const initialSearchText = React.useMemo(() => {
        const params = new URLSearchParams(location.search);
        const firstName = params.get('firstName') ?? '';
        const lastName = params.get('lastName') ?? '';
        return [firstName, lastName].filter(Boolean).join(' ').trim();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Local state ─────────────────────────────────────────────────────────────

    const [loading, setLoading] = React.useState({ leads: true, initialLoading: true });
    const [operation, setOperation] = React.useState<'idle' | 'saving' | 'converting'>('idle');

    const isSaving = operation === 'saving';
    const isConverting = operation === 'converting';

    const [pageInfo, setPageInfo] = React.useState<{
        page: number;
        size: number;
        leadStages: string[];
        leadSources: string[];
        query: string;
        order: string;
        filterOperator: 'and' | 'or' | undefined;
        createdFrom: string | undefined;
        createdTo: string | undefined;
        lastContactedFrom: string | undefined;
        lastContactedTo: string | undefined;
    }>({
        page: 1,
        size: 10,
        leadStages: [],
        leadSources: [],
        query: initialSearchText,
        order: '',
        filterOperator: undefined,
        createdFrom: undefined,
        createdTo: undefined,
        lastContactedFrom: undefined,
        lastContactedTo: undefined
    });

    const [searchText, setSearchText] = React.useState(initialSearchText);
    const [searchInputKey, setSearchInputKey] = React.useState(0);

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const firstName = params.get('firstName') ?? '';
        const lastName = params.get('lastName') ?? '';
        const text = [firstName, lastName].filter(Boolean).join(' ').trim();
        if (!text) return;
        setSearchText(text);
        setSearchInputKey(k => k + 1);
        setPageInfo(prev => (prev.query === text ? prev : { ...prev, query: text, page: 1 }));
    }, [location.search]);

    const [showAddLead, setShowAddLead] = React.useState(false);
    const [editingLead, setEditingLead] = React.useState<Client | null>(null);

    const [showFilterLead, setShowFilterLead] = React.useState(false);
    const [draftFilters, setDraftFilters] = React.useState<FilterDrawerState>(defaultFilterDrawerState);
    const [appliedFilters, setAppliedFilters] = React.useState<FilterDrawerState>(defaultFilterDrawerState);

    const [showNewBooking, setShowNewBooking] = React.useState<BookingNew | undefined>(undefined);
    const [smsClient, setSmsClient] = React.useState<Client | null>(null);
    const [showShareSendPanel, setShowShareSendPanel] = React.useState(false);
    const [shareSendLead, setShareSendLead] = React.useState<Client | null>(null);
    const [convertingLead, setConvertingLead] = React.useState<Client | null>(null);
    const [detailLead, setDetailLead] = React.useState<Client | null>(null);

    // ─── API calls ────────────────────────────────────────────────────────────────

    const fetchStagesCount = React.useCallback(() => {
        dispatch(fetchLeadsStagesCountThunk(apiClientWrapper));
    }, []);

    const fetchLeads = React.useCallback(() => {
        dispatch(
            fetchLeadsThunk(apiClientWrapper, {
                page: pageInfo.page,
                size: pageInfo.size,
                lead_stage: pageInfo.leadStages.length > 0 ? pageInfo.leadStages : undefined,
                lead_source: pageInfo.leadSources.length > 0 ? pageInfo.leadSources : undefined,
                query: pageInfo.query || undefined,
                order: pageInfo.order || undefined,
                filters_operator: pageInfo.filterOperator,
                created_from: pageInfo.createdFrom,
                created_to: pageInfo.createdTo,
                last_contacted_from: pageInfo.lastContactedFrom,
                last_contacted_to: pageInfo.lastContactedTo
            })
        );
    }, [pageInfo]);

    React.useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    React.useEffect(() => {
        fetchStagesCount();
    }, []);

    React.useEffect(() => {
        return () => {
            dispatch(clearLeadsThunk());
        };
    }, [dispatch]);

    // ─── Status tracking ──────────────────────────────────────────────────────────

    React.useEffect(() => {
        if (status === ClientsStatus.LeadsFetchSuccess || status === ClientsStatus.Error) {
            setLoading(prev => ({ ...prev, leads: false, initialLoading: false }));
        }
        if (status === ClientsStatus.LeadsFetching) {
            setLoading(prev => ({ ...prev, leads: true }));
        }
        if (status === ClientsStatus.SaveSuccess) {
            setOperation('idle');
            setConvertingLead(null);
            closeForm();
            refreshLeads();
            fetchStagesCount();
        }
        if (status === ClientsStatus.LogicalDeleteSuccess) {
            refreshLeads();
            fetchStagesCount();
        }
        if (status === ClientsStatus.Error) {
            setOperation('idle');
        }
    }, [status, fetchStagesCount]);

    // ─── Derived values ───────────────────────────────────────────────────────────

    const totalPages = leadsTotalPages ?? 1;
    const totalRecords = leadsTotalRecords ?? 0;
    const hasMorePages = pageInfo.page < totalPages;

    const appliedFiltersCount = React.useMemo(
        () =>
            [
                appliedFilters.creationDate.enabled,
                appliedFilters.lastContacted.enabled,
                appliedFilters.source.enabled,
                appliedFilters.stage.enabled
            ].filter(Boolean).length,
        [appliedFilters]
    );

    const stageCounts = React.useMemo<Record<string, number>>(
        () => ({
            new: leadsStagesCount?.new ?? 0,
            contacted: leadsStagesCount?.contacted ?? 0,
            booked: leadsStagesCount?.booked ?? 0,
            won: leadsStagesCount?.won ?? 0,
            lost: leadsStagesCount?.lost ?? 0
        }),
        [leadsStagesCount]
    );

    // ─── Handlers ─────────────────────────────────────────────────────────────────

    const handleStageChange = React.useCallback((stage: string) => {
        setDraftFilters(defaultFilterDrawerState);
        setAppliedFilters(defaultFilterDrawerState);
        setPageInfo(prev => ({
            ...prev,
            page: 1,
            leadStages: stage === 'all' ? [] : [stage],
            leadSources: [],
            filterOperator: undefined,
            createdFrom: undefined,
            createdTo: undefined,
            lastContactedFrom: undefined,
            lastContactedTo: undefined
        }));
    }, []);

    const handleSourceChange = React.useCallback((source: string) => {
        setPageInfo(prev => ({ ...prev, leadSources: source === 'all' ? [] : [source], page: 1 }));
    }, []);

    const handleSearchChange = useDebouncedCallback((text: string) => {
        setPageInfo(prev => ({ ...prev, query: text, page: 1 }));
    }, 500);

    const handleSearchTextChange = React.useCallback(
        (text: string) => {
            setSearchText(text);
            handleSearchChange(text);
        },
        [handleSearchChange]
    );

    const handleOrderChange = React.useCallback((order: string) => {
        setPageInfo(prev => ({ ...prev, order, page: 1 }));
    }, []);

    const DATE_FORMAT = 'YYYY-MM-DD';

    const handleApplyFilterDrawer = React.useCallback((state: FilterDrawerState) => {
        setAppliedFilters(state);
        setPageInfo(prev => ({
            ...prev,
            page: 1,
            leadStages:
                state.stage.enabled && state.stage.selectedStages.length > 0
                    ? state.stage.selectedStages.map(s => s.toLowerCase())
                    : [],
            leadSources:
                state.source.enabled && state.source.selectedSources.length > 0
                    ? state.source.selectedSources.map(s => s.toLowerCase())
                    : [],
            filterOperator: state.filterOperator,
            createdFrom:
                state.creationDate.enabled && state.creationDate.period
                    ? moment(state.creationDate.period.from).format(DATE_FORMAT)
                    : undefined,
            createdTo:
                state.creationDate.enabled && state.creationDate.period
                    ? moment(state.creationDate.period.to).format(DATE_FORMAT)
                    : undefined,
            lastContactedFrom:
                state.lastContacted.enabled && state.lastContacted.period
                    ? moment(state.lastContacted.period.from).format(DATE_FORMAT)
                    : undefined,
            lastContactedTo:
                state.lastContacted.enabled && state.lastContacted.period
                    ? moment(state.lastContacted.period.to).format(DATE_FORMAT)
                    : undefined
        }));
    }, []);

    const handleClearFilterDrawer = React.useCallback(() => {
        setDraftFilters(defaultFilterDrawerState);
        setAppliedFilters(defaultFilterDrawerState);
        setPageInfo(prev => ({
            ...prev,
            page: 1,
            leadStages: [],
            leadSources: [],
            filterOperator: undefined,
            createdFrom: undefined,
            createdTo: undefined,
            lastContactedFrom: undefined,
            lastContactedTo: undefined
        }));
    }, []);

    const setPage = React.useCallback((page: number) => {
        setPageInfo(prev => ({ ...prev, page }));
    }, []);

    const setSize = React.useCallback((size: number) => {
        setPageInfo(prev => ({ ...prev, size, page: 1 }));
    }, []);

    const refreshLeads = React.useCallback(() => {
        setPageInfo(prev => ({ ...prev, page: 1 }));
    }, []);

    const openAddForm = React.useCallback(() => {
        setEditingLead(null);
        setShowAddLead(true);
    }, []);

    const openEditForm = React.useCallback((lead: Client) => {
        setEditingLead(lead);
        setShowAddLead(true);
    }, []);

    const closeForm = React.useCallback(() => {
        setShowAddLead(false);
        setEditingLead(null);
    }, []);

    const openFilterDrawer = React.useCallback(() => {
        setDraftFilters(appliedFilters);
        setShowFilterLead(true);
    }, [appliedFilters]);

    const closeFilterDrawer = React.useCallback(() => {
        setDraftFilters(appliedFilters);
        setShowFilterLead(false);
    }, [appliedFilters]);

    const openBooking = React.useCallback((lead: Client) => {
        setShowNewBooking({ clientId: lead.id!, parentName: `${lead.firstName} ${lead.lastName}` });
    }, []);

    const closeBooking = React.useCallback(() => {
        setShowNewBooking(undefined);
    }, []);

    const updateBookingPet = React.useCallback((petId: number) => {
        setShowNewBooking(prev => (prev ? { ...prev, petId } : prev));
    }, []);

    const openSms = React.useCallback((lead: Client) => {
        setSmsClient(lead);
    }, []);

    const closeSms = React.useCallback(() => {
        setSmsClient(null);
    }, []);

    const openShareSendPanel = React.useCallback((lead: Client) => {
        setShareSendLead(lead);
        setShowShareSendPanel(true);
    }, []);

    const closeShareSendPanel = React.useCallback(() => {
        setShowShareSendPanel(false);
        setShareSendLead(null);
    }, []);

    const saveLead = React.useCallback(
        (lead: Client) => {
            setOperation('saving');
            dispatch(saveClientThunk(apiClientWrapper, lead));
        },
        [apiClientWrapper, dispatch]
    );

    const deleteLead = React.useCallback(
        (leadId: number) => {
            dispatch(logicalDeleteClientThunk(apiClientWrapper, leadId));
        },
        [apiClientWrapper, dispatch]
    );

    const openDetail = React.useCallback((lead: Client) => {
        setDetailLead(lead);
    }, []);

    const closeDetail = React.useCallback(() => {
        setDetailLead(null);
    }, []);

    const openConvert = React.useCallback((lead: Client) => {
        if (lead.clientSince != null) return;
        setConvertingLead(lead);
    }, []);

    const closeConvert = React.useCallback(() => {
        setConvertingLead(null);
    }, []);

    const convertLead = React.useCallback(
        (lead: Client) => {
            // Conversion is non-reversible: only proceed for actual leads and never dispatch twice.
            if (lead.clientSince != null || isConverting) return;
            setOperation('converting');
            dispatch(
                saveClientThunk(
                    apiClientWrapper,
                    {
                        ...lead,
                        clientSince: moment(),
                        leadStage: LeadStage.Won,
                        conversionSource: ConversionSource.Manual
                    },
                    false
                )
            );
        },
        [apiClientWrapper, dispatch, isConverting]
    );

    // ─── Memoized context values ──────────────────────────────────────────────────

    const dataValue = React.useMemo<LeadsDataContextProps>(
        () => ({
            leads: leads ?? [],
            isLoading: loading.leads,
            isFirstRender: loading.initialLoading,
            status,
            page: pageInfo.page,
            size: pageInfo.size,
            totalPages,
            totalRecords,
            hasMorePages,
            setPage,
            setSize,
            stageCounts,
            selectedStage: pageInfo.leadStages.length === 1 ? pageInfo.leadStages[0] : 'all',
            handleStageChange,
            selectedSource: pageInfo.leadSources.length === 1 ? pageInfo.leadSources[0] : 'all',
            handleSourceChange,
            searchText,
            searchInputKey,
            handleSearchChange: handleSearchTextChange,
            order: pageInfo.order,
            handleOrderChange,
            appliedFiltersCount,
            hasActiveFilters:
                (pageInfo.leadStages.length === 1 ? pageInfo.leadStages[0] : 'all') !== 'all' ||
                (pageInfo.leadSources.length === 1 ? pageInfo.leadSources[0] : 'all') !== 'all' ||
                !!searchText ||
                appliedFiltersCount > 0,
            isGlobalEmpty:
                totalRecords === 0 &&
                pageInfo.leadStages.length === 0 &&
                pageInfo.leadSources.length === 0 &&
                !searchText &&
                appliedFiltersCount === 0
        }),
        [
            leads,
            loading,
            status,
            pageInfo,
            totalPages,
            totalRecords,
            hasMorePages,
            setPage,
            setSize,
            stageCounts,
            handleStageChange,
            handleSourceChange,
            searchText,
            searchInputKey,
            handleSearchTextChange,
            handleOrderChange,
            appliedFiltersCount
        ]
    );

    const actionsValue = React.useMemo<LeadsActionsContextProps>(
        () => ({
            saveLead,
            deleteLead,
            convertLead,
            isConverting,
            refreshLeads,
            fetchStagesCount,
            isSaving,
            openAddForm,
            openEditForm,
            openConvert,
            openBooking,
            openSms,
            openShareSendPanel,
            openDetail,
            closeDetail
        }),
        [
            saveLead,
            deleteLead,
            convertLead,
            isConverting,
            refreshLeads,
            fetchStagesCount,
            isSaving,
            openAddForm,
            openEditForm,
            openConvert,
            openBooking,
            openSms,
            openShareSendPanel,
            openDetail,
            closeDetail
        ]
    );

    const uiValue = React.useMemo<LeadsUIContextProps>(
        () => ({
            showAddLead,
            editingLead,
            closeForm,
            showFilterLead,
            openFilterDrawer,
            closeFilterDrawer,
            draftFilters,
            setDraftFilters,
            appliedFilters,
            handleApplyFilterDrawer,
            handleClearFilterDrawer,
            showNewBooking,
            closeBooking,
            updateBookingPet,
            smsClient,
            closeSms,
            showShareSendPanel,
            shareSendLead,
            closeShareSendPanel,
            convertingLead,
            closeConvert,
            // Re-read from the refreshed list so an edit or conversion made while the
            // detail is open is reflected, falling back to the captured lead if the
            // list is momentarily empty (e.g. mid-refetch).
            detailLead: detailLead ? (leads ?? []).find(lead => lead.id === detailLead.id) ?? detailLead : null
        }),
        [
            detailLead,
            leads,
            showAddLead,
            editingLead,
            closeForm,
            showFilterLead,
            openFilterDrawer,
            closeFilterDrawer,
            draftFilters,
            setDraftFilters,
            appliedFilters,
            handleApplyFilterDrawer,
            handleClearFilterDrawer,
            showNewBooking,
            closeBooking,
            updateBookingPet,
            smsClient,
            closeSms,
            showShareSendPanel,
            shareSendLead,
            closeShareSendPanel,
            convertingLead,
            closeConvert
        ]
    );

    return (
        <LeadsDataContext.Provider value={dataValue}>
            <LeadsActionsContext.Provider value={actionsValue}>
                <LeadsUIContext.Provider value={uiValue}>{children}</LeadsUIContext.Provider>
            </LeadsActionsContext.Provider>
        </LeadsDataContext.Provider>
    );
};

// ─── Hooks ─────────────────────────────────────────────────────────────────────

export const useLeadsData = (): LeadsDataContextProps => {
    const context = React.useContext(LeadsDataContext);
    if (!context) throw new Error('useLeadsData must be used within a LeadsProvider');
    return context;
};

export const useLeadsActions = (): LeadsActionsContextProps => {
    const context = React.useContext(LeadsActionsContext);
    if (!context) throw new Error('useLeadsActions must be used within a LeadsProvider');
    return context;
};

export const useLeadsUI = (): LeadsUIContextProps => {
    const context = React.useContext(LeadsUIContext);
    if (!context) throw new Error('useLeadsUI must be used within a LeadsProvider');
    return context;
};
